import {kebabCase, onBlockLoaded} from "@plaudit/frontend-apis";

const selector_trigger = '.accordion__heading';
const selector_target = '.accordion__content';
const open_marker = 'accordion--open';
let accordionNumber = 0;

function setupIconAndButton(accordionTrigger: HTMLElement, accordionContentId: string) {
	let button: HTMLButtonElement|null;
	if (!(button = accordionTrigger.querySelector("button"))) {
		button = document.createElement("button");
		// This cloning is done to allow us to override the SVG via a PHP filter.
		const svgElement = document.getElementById("plaudit-accordion-block-toggle-svg")?.cloneNode(true) as HTMLElement|undefined;
		if (svgElement === undefined) {
			return button;
		}
		svgElement.removeAttribute("id");
		button.appendChild(svgElement);

		button.setAttribute("aria-expanded", "false");
		button.setAttribute("aria-controls", accordionContentId);
		while (accordionTrigger.firstChild) {
			button.appendChild(accordionTrigger.firstChild);
		}
		accordionTrigger.appendChild(button);
	}
	return button;
}

function attachAccordionTransitionEvents(accordionContent: HTMLElement) {
	accordionContent.addEventListener('transitionrun', evt => {
		if (evt.propertyName === 'max-height' && evt.target === accordionContent) {
			accordionContent.dataset.plauditAccordionTransitionCount = (parseInt(accordionContent.dataset.plauditAccordionTransitionCount!) + 1).toString();
		}
	}, {passive: true});
	const onEnd = (evt: TransitionEvent) => {
		if (evt.propertyName === 'max-height' && evt.target === accordionContent) {
			if (evt.type === 'transitionend' && (accordionContent.style.maxHeight === "0px" || accordionContent.style.maxHeight === "0")) {
				accordionContent.style.display = 'none';
			}
			accordionContent.dataset.plauditAccordionTransitionCount = (parseInt(accordionContent.dataset.plauditAccordionTransitionCount!) - 1).toString();
		}
	};
	accordionContent.addEventListener('transitionend', onEnd, {passive: true});
	accordionContent.addEventListener('transitioncancel', onEnd, {passive: true});
}

function toggleAccordionContent(accordionContent: HTMLElement, open: boolean): void {
	if (open) {
		const computedStyle = getComputedStyle(accordionContent);
		accordionContent.style.display = computedStyle.getPropertyValue("--wp--custom--accordion-content-display") || "block";
		accordionContent.style.maxHeight = accordionContent.scrollHeight + "px";
	} else {
		// While we could set display to none here if FireFox had actually implemented support for "transition-behavior", they haven't as of 07/24/2025
		// See here: https://caniuse.com/mdn-css_properties_display_is_transitionable
		// Instead, we set it within the transitionend listener in attachAccordionTransitionEvents()
		accordionContent.style.maxHeight = "0px";
	}
}

function updateURLFragment(accordion: Element, open: boolean) {
	if (open) {
		// Set the fragment to the id of the accordion we are opening
		const url = new URL(window.location.href);
		url.hash = accordion.id;
		history.replaceState(history.state, "", url);
	} else {
		// If the current fragment is the id of the accordion we are closing, remove it from the URL
		if (window.location.hash && window.location.hash.substring(1) === accordion.id) {
			const url = new URL(window.location.href);
			url.hash = document.querySelector(`.wp-block-plaudit-block-library-accordion.${open_marker}`)?.id ?? "";
			history.replaceState(history.state, "", url);
		}
	}
}

const resizeObserver = new ResizeObserver(entries => {
	for (const entry of entries) {
		const element = entry.target;
		if (
			element instanceof HTMLElement && element.dataset.plauditAccordionTransitionCount === "0"
			&& element.style.maxHeight && element.style.maxHeight !== "0px" && element.style.maxHeight !== "0"
		) {
			element.style.maxHeight = ""; // We have to unset the maxHeight before recalculating the height, otherwise it can never increase
			element.style.maxHeight = entry.target.scrollHeight + "px";
		}
	}
});

onBlockLoaded({
	blockOrClassName: "plaudit-block-library/accordion",
	onAdd(accordion) {
		const accordionContent = accordion.querySelector<HTMLElement>(selector_target);
		const accordionTrigger = accordion.querySelector<HTMLElement>(selector_trigger);
		if (accordionContent && accordionTrigger) {
			accordion.addEventListener('click', event => {
				let button: HTMLButtonElement|null;
				if (event.target instanceof Element && (button = event.target.closest("button"))) {
					// The chain of "closest"s ensures that we only proceed when the click event comes from within the head
					const accordion = event.target.closest(selector_trigger)?.closest(".wp-block-plaudit-block-library-accordion");
					if (accordion) {
						event.preventDefault();
						event.stopPropagation();

						const open = accordion.classList.toggle(open_marker);
						button.setAttribute("aria-expanded", open.toString());

						const accordionContentId = button.getAttribute("aria-controls");
						if (accordionContentId) {
							const accordionContent = document.getElementById(accordionContentId);
							if (accordionContent) {
								toggleAccordionContent(accordionContent, open);
								updateURLFragment(accordion, open);
							}
						}
					}
				}
			});

			accordionContent.dataset.plauditAccordionTransitionCount = "0";
			resizeObserver.observe(accordionContent);

			let accordionId = accordion.id;
			if (!accordionId.length) {
				accordionId = kebabCase(accordionTrigger.innerText.trim());

				if (!accordionId) {
					accordionId = `p-accordion-${++accordionNumber}`;
				} else if (document.getElementById(accordionId)) { // We use else-if because we only need to check for overlap when we're using an id based on the trigger's contents
					// If the generated id would overlap with the id of another element on the page, we append a suffix to ensure that it remains unique
					accordionId += "-" + (++accordionNumber);
				}
				accordion.id = accordionId;
			}
			if (!accordionContent.id) {
				accordionContent.id = `${accordionId}-content`;
			}

			const button = setupIconAndButton(accordionTrigger, accordionContent.id);

			const open = !!window.location.hash && accordionId === window.location.hash.substring(1);
			button.setAttribute("aria-expanded", open.toString());
			accordion.classList.toggle(open_marker, open);
			// We can safely call this because we're explicitly disabling the transitions
			toggleAccordionContent(accordionContent, open);
			if (!open) {
				// We have to set this here because we cannot set it within toggleAccordionContent() until FireFox fixes their "support" for "transition-behavior"
				accordionContent.style.display = 'none';
			}
			attachAccordionTransitionEvents(accordionContent);
		}
	}
});
